import { AXIOS_NETWORK_PATTERNS } from "common/utils/app-monitoring/network-error-patterns";

export type BenignErrorInput = {
  message?: string | null;
  type?: string | null;
  status?: number | null;
};

// 4xx the backend returns with a user-facing reason already surfaced in the UI.
// Reporting these to Honeybadger adds noise without adding actionable signal.
const VALIDATION_STATUSES = new Set([400, 401, 403, 404, 413, 422]);

// Infrastructure-side failures (CloudFront / LB / upstream). Tracked via
// backend faults (Faraday::TimeoutError, HTTPClient::ReceiveTimeoutError, etc.)
// so double-reporting from the client only obscures the residual.
const INFRA_STATUSES = new Set([499, 502, 503, 504, 520, 521, 522, 523, 524]);

// Rate-limit / throttling responses. Rails `rack.attack` and CloudFront WAF
// already emit structured signals (AppMonitoring.notify + StatsD counters) for
// the producer side, so client-side notifications add only noise.
const THROTTLING_STATUSES = new Set([429]);

const ABORT_TYPES = new Set(["AbortError", "CanceledError"]);

export function isBenignUploadError({ message, type, status }: BenignErrorInput): boolean {
  // When an HTTP status is present it is authoritative: either the response was
  // a known-benign 4xx/infra 5xx/throttle, or it is a real server error whose
  // body message must not be allowed to reclassify it as benign via pattern
  // match.
  if (status != null) {
    if (VALIDATION_STATUSES.has(status)) return true;
    if (INFRA_STATUSES.has(status)) return true;
    if (THROTTLING_STATUSES.has(status)) return true;
    return false;
  }
  if (typeof type === "string" && ABORT_TYPES.has(type)) return true;
  if (typeof message === "string" && message.trim() !== "") {
    if (AXIOS_NETWORK_PATTERNS.some(pattern => pattern.test(message))) return true;
  }
  return false;
}
